import clsx from 'clsx'
import React, { useEffect, useRef } from 'react'

import { ReactComponent as ArrowRight } from 'assets/icons/arrow-right.svg'
import { Button } from 'components/common/button/Button'
import { Icon } from 'components/uikit/Icon/Icon'
import { Tooltip } from 'components/uikit/Tooltip'
import { AnalyticsActionType } from 'types/analytics.type'
import { HighLevelExperiences, HighLevelSkills } from 'types/practitioners'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './PractitionersListDetailedView.module.scss'

interface Props {
  isOpen?: boolean
  highLevelExperiences?: HighLevelExperiences[]
  highLevelSkills?: HighLevelSkills[]
  email?: string
  showExpProfileCta?: boolean
}

const PractitionersListDetailedView = ({
  isOpen = false,
  highLevelExperiences,
  highLevelSkills,
  email,
  showExpProfileCta,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const wrapper = wrapperRef?.current

    if (wrapper) {
      if (isOpen) {
        wrapper.style.maxHeight = `${wrapper.scrollHeight}px`
      } else {
        wrapper.style.maxHeight = '0'
      }
    }
  }, [isOpen])

  return (
    <div
      ref={wrapperRef}
      className={clsx('tour-staffingrecommendations-projectselection', styles.wrapper, {
        [styles.visible]: isOpen,
        [styles.emptyStateCollapsible]: !highLevelExperiences?.length,
      })}
    >
      <div className={styles.collapsibleContent}>
        {/* A user cannot have skills without experiences */}
        {highLevelExperiences?.length ? (
          <>
            {!!highLevelSkills?.length && (
              <div className={clsx(styles.grid)}>
                <div className={clsx(styles.heading, 'col-span-2')}>Skills Averaging Level 4 or Above</div>
                {highLevelSkills?.map(skill => (
                  <React.Fragment key={skill.id}>
                    {!!skill.topExperiences?.length ? (
                      <div key={skill.id} className={clsx(styles.item)} style={{ borderColor: skill.capColor }}>
                        <Tooltip config={{ allowHTML: true }}>
                          <div className={styles.itemText}>{skill?.name}</div>
                          <div slot="tooltip-content" className={styles.tooltip}>
                            <b>Top Experience Within This Skill:</b>
                            {skill.topExperiences.map(topXp => (
                              <div key={topXp?.id}>
                                <span>{topXp.experience?.name}</span>
                                <br />
                              </div>
                            ))}
                          </div>
                        </Tooltip>
                      </div>
                    ) : (
                      <div key={skill.id} className={clsx(styles.item)} style={{ borderColor: skill.capColor }}>
                        <div className={styles.itemText}>{skill.name}</div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className={clsx(styles.grid)}>
              <div className={clsx(styles.heading, 'col-span-2')}>Experiences Averaging Level 4 or Above</div>
              {highLevelExperiences?.length &&
                highLevelExperiences.map(exp => (
                  <div key={exp.id} className={clsx(styles.item)} style={{ borderColor: exp.experience.color }}>
                    <div className={styles.itemText}>{exp.experience.name}</div>
                  </div>
                ))}
            </div>
          </>
        ) : (
          <div className={clsx(styles.heading, { [styles.emptyState]: !highLevelSkills?.length })}>
            This user doesn't have skills or experiences averaging Level 4 or above.
          </div>
        )}
      </div>
      {showExpProfileCta && (
        <Button
          className={styles.btnViewExperienceProfile}
          text="Visit Experience Profile"
          iconRight={<Icon icon={ArrowRight} />}
          onClick={() => {
            window.open(`${process.env.REACT_APP_EXPERIENCE_PROFILE_BASE_URL}/users/${email}/public`)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: ANALYTICS_EVENTS.PRACTITIONERS_LIST_EXP_PROFILE_CLICKED,
            })
          }}
        />
      )}
    </div>
  )
}

export default PractitionersListDetailedView
